import { validateCablePath, resolvePortNodeId } from '../engine/cables.js';
import { cellKey, cellsAdjacent, virtualGridDimsForCamera } from '../engine/grid.js';
import { Simulation } from '../engine/simulation.js';
import type { CellCoord, MapId, PermanentUpgrades, SimulationEvent } from '../engine/types.js';
import { stretchTipToward } from '../render/camera.js';
import { GameRenderer } from '../render/GameRenderer.js';
import { AudioManager } from '../audio/AudioManager.js';
import type { GameSettings } from './settings.js';
import type { ApiClient } from '../api/client.js';

export type BuildTool = 'cable' | 'relay' | 'switch' | 'teleporter' | 'erase';

const AUTOSAVE_KEY = 'pulsenetwork.autosave';

export interface GameUiCallbacks {
  onHudUpdate: () => void;
  onWeekReward: (week: number) => void;
  onGameOver: (day: number, score: number) => void;
  onTutorialDone: () => void;
  onEscapeMenu: () => void;
}

export class GameController {
  sim: Simulation;
  renderer: GameRenderer;
  tool: BuildTool = 'cable';
  private raf = 0;
  private lastTs = 0;
  private autosaveTimer = 0;
  private drawing = false;
  private path: CellCoord[] = [];
  private lastPointer: { x: number; y: number } | null = null;
  private startNodeId: string | null = null;
  /** Cable committed cell-by-cell while drawing (survives backtrack of last cell). */
  private draftCableId: string | null = null;
  /** Active pointer id while drawing/erasing (touch-safe). */
  private activePointerId: number | null = null;
  /** Right-click / erase-tool drag: paint-erase cell by cell. */
  private erasing = false;
  private lastEraseKey: string | null = null;
  private keys = new Set<string>();
  private devMode: boolean;
  private unsubSim: (() => void) | null = null;
  private destroyed = false;
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (this.destroyed) return;
    this.keys.add(e.key.toLowerCase());
    // Escape is handled once by App (cancel draw / pause menu).
    if (e.code === 'Space') {
      e.preventDefault();
      this.togglePause();
    }
    // AZERTY top-row: & é "  (+ Digit1/2/3 and plain 1/2/3)
    if (e.key === '1' || e.key === '&' || e.code === 'Digit1') this.setSpeed(1);
    if (e.key === '2' || e.key === 'é' || e.code === 'Digit2') this.setSpeed(2);
    if (e.key === '3' || e.key === '"' || e.key === '“' || e.code === 'Digit3') {
      this.setSpeed(3);
    }
    if (this.devMode && e.key === 'n') {
      this.sim.simulateFor(20);
    }
  };
  private readonly onKeyUp = (e: KeyboardEvent): void => {
    if (this.destroyed) return;
    this.keys.delete(e.key.toLowerCase());
  };
  private readonly onWindowPointerUp = (e: PointerEvent): void => {
    if (this.destroyed) return;
    // Only care about our active stroke; ignore HUD taps / other fingers.
    if (this.activePointerId !== null && e.pointerId !== this.activePointerId) return;
    if (e.target instanceof HTMLCanvasElement) return;
    if (this.drawing || this.erasing) {
      // Finger lifted outside canvas — commit what we have (tablet often misses canvas up).
      const pos = this.lastPointer;
      if (this.drawing && pos) this.onPointerUp(pos.x, pos.y);
      else this.cancelDraw({ discardDraft: true });
    }
  };
  private readonly onWindowPointerCancel = (e: PointerEvent): void => {
    if (this.destroyed) return;
    if (this.activePointerId !== null && e.pointerId !== this.activePointerId) return;
    // Commit in-progress cable instead of silently dropping it (common on iOS).
    const pos = this.lastPointer;
    if (this.drawing && pos) this.onPointerUp(pos.x, pos.y);
    else this.cancelDraw({ discardDraft: true });
  };
  showTutorial: boolean;

  constructor(
    canvas: HTMLCanvasElement,
    mapId: MapId,
    upgrades: PermanentUpgrades,
    settings: GameSettings,
    private audio: AudioManager,
    private api: ApiClient,
    private callbacks: GameUiCallbacks,
    resumeSave?: string | null,
  ) {
    this.devMode = new URLSearchParams(location.search).get('dev') === '1';
    if (resumeSave) {
      try {
        const data = JSON.parse(resumeSave);
        this.sim = Simulation.deserialize(data);
      } catch {
        this.sim = Simulation.createNewRun(mapId, upgrades);
      }
    } else {
      this.sim = Simulation.createNewRun(mapId, upgrades);
    }
    this.renderer = new GameRenderer(canvas, settings);
    this.showTutorial = !api.getProfile().tutorialDone && this.sim.state.cables.length === 0;
    this.renderer.overlay.tutorialHighlight = this.showTutorial;
    // Pause until the first cable so newcomers can learn without an early game-over.
    if (this.showTutorial) {
      this.sim.state.paused = true;
    }

    this.unsubSim = this.sim.on((ev) => this.handleEvent(ev));
    this.bindInput(canvas);
    this.bindKeyboard();
  }

  start(): void {
    this.renderer.resize();
    this.syncCamera();
    this.lastTs = performance.now();
    const loop = (ts: number) => {
      const dt = Math.min(0.05, (ts - this.lastTs) / 1000);
      this.lastTs = ts;
      this.tick(dt);
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
    window.addEventListener('resize', () => {
      this.renderer.resize();
      this.syncCamera();
    });
  }

  /** Always frame the full grid; dezoom lerps toward the next expansion. */
  private syncCamera(): void {
    const s = this.sim.state;
    const virt = virtualGridDimsForCamera(
      s.day,
      s.simTime,
      s.gridWidth,
      s.gridHeight,
    );
    this.renderer.camera.fitEvolvingGrid(
      s.gridWidth,
      s.gridHeight,
      virt.width,
      virt.height,
      this.renderer.canvas.clientWidth,
      this.renderer.canvas.clientHeight,
    );
  }

  /**
   * @param keepSave — false after game-over / abandon so the map resets for a fresh run.
   */
  destroy(keepSave = true): void {
    this.destroyed = true;
    cancelAnimationFrame(this.raf);
    this.unsubSim?.();
    window.removeEventListener('keydown', this.onKeyDown, true);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('pointerup', this.onWindowPointerUp);
    window.removeEventListener('pointercancel', this.onWindowPointerCancel);
    if (keepSave && !this.sim.state.gameOver) this.saveAutosave();
    else GameController.clearAutosave();
  }

  private tick(dt: number): void {
    const events = this.sim.advance(dt);
    for (const ev of events) this.handleEvent(ev);

    this.autosaveTimer += dt;
    if (this.autosaveTimer > 15) {
      this.autosaveTimer = 0;
      this.saveAutosave();
      void this.api.saveRun(this.sim.serialize());
    }

    this.syncCamera();
    this.renderer.render(this.sim, dt);
    this.callbacks.onHudUpdate();
  }

  private handleEvent(ev: SimulationEvent): void {
    switch (ev.type) {
      case 'pulseDelivered':
        this.renderer.deliveryFlash(ev.nodeId, this.sim);
        break;
      case 'nodeSpawned':
        this.renderer.noteSpawn(ev.node.id);
        if (ev.node.kind === 'generator') this.audio.playSfx('spawnGen');
        else if (ev.node.kind === 'bulb') this.audio.playSfx('spawnBulb');
        break;
      case 'weekReward':
        this.callbacks.onWeekReward(ev.week);
        break;
      case 'gameOver':
        this.audio.playSfx('defeat');
        // Scoring / API finish handled by App.onGameOver to avoid double awards.
        this.callbacks.onGameOver(ev.day, ev.score);
        break;
      case 'gridExpanded':
        this.syncCamera();
        break;
      default:
        break;
    }
  }

  setTool(t: BuildTool): void {
    this.tool = t;
    this.cancelDraw();
  }

  togglePause(): void {
    if (this.sim.state.gameOver) return;
    if (this.sim.state.paused) this.sim.resume();
    else this.sim.pause();
  }

  setSpeed(s: 1 | 2 | 3): void {
    this.sim.setSpeed(s);
  }

  chooseReward(index: number): void {
    this.releasePointerState();
    const offer = this.sim.state.rewardOffers?.[index];
    this.sim.chooseReward(index);
    this.audio.playSfx('reward');
    if (offer?.bonus.kind === 'teleporter' && this.sim.state.teleporterPairsRemaining > 0) {
      this.tool = 'teleporter';
    } else if (
      (offer?.bonus.kind === 'extraSwitches' || offer?.bonus.kind === 'switch') &&
      this.sim.state.switchesRemaining > 0
    ) {
      this.tool = 'switch';
    } else if (offer?.bonus.kind === 'extraRelays' && this.sim.state.relaysRemaining > 0) {
      this.tool = 'relay';
    }
  }

  saveAutosave(): void {
    localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(this.sim.serialize()));
  }

  static loadAutosave(): string | null {
    return localStorage.getItem(AUTOSAVE_KEY);
  }

  static clearAutosave(): void {
    localStorage.removeItem(AUTOSAVE_KEY);
  }

  private cancelDraw(opts?: { discardDraft?: boolean }): void {
    if (opts?.discardDraft && this.draftCableId) {
      this.sim.eraseCable(this.draftCableId);
    }
    this.drawing = false;
    this.erasing = false;
    this.lastEraseKey = null;
    this.path = [];
    this.lastPointer = null;
    this.startNodeId = null;
    this.draftCableId = null;
    this.activePointerId = null;
    this.renderer.overlay.previewPath = null;
    this.renderer.overlay.draftStretch = null;
  }

  /** Reset pointer/draw state after modals (reward pick can swallow pointerup). */
  releasePointerState(): void {
    this.cancelDraw();
  }

  /** @returns true if a draw/erase was cancelled. */
  cancelActiveDraw(): boolean {
    if (!this.drawing && !this.erasing) return false;
    this.cancelDraw({ discardDraft: true });
    return true;
  }

  private bindKeyboard(): void {
    // Capture phase so ESC works even when a button/modal has focus.
    window.addEventListener('keydown', this.onKeyDown, true);
    window.addEventListener('keyup', this.onKeyUp);
    // If pointer is released outside the canvas (modal/HUD), clear sticky draw.
    window.addEventListener('pointerup', this.onWindowPointerUp);
    window.addEventListener('pointercancel', this.onWindowPointerCancel);
  }

  private bindInput(canvas: HTMLCanvasElement): void {
    const getPos = (e: PointerEvent | Touch) => {
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    /** Touch/pen primary contact — some browsers report button=-1 on up. */
    const isPrimaryDraw = (e: PointerEvent): boolean =>
      e.pointerType === 'touch' ||
      e.pointerType === 'pen' ||
      e.button === 0 ||
      (e.button === -1 && (this.drawing || this.activePointerId === e.pointerId));

    canvas.style.touchAction = 'none';
    // Prevent iOS/Android from hijacking the stroke as a page scroll / gesture.
    canvas.style.userSelect = 'none';
    (canvas.style as CSSStyleDeclaration & { webkitUserSelect?: string }).webkitUserSelect =
      'none';

    // Full-grid framing only — no manual zoom/pan.
    canvas.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
      },
      { passive: false },
    );

    const stopNative = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // Belt-and-suspenders: no browser menu / tab gestures on the playfield.
    canvas.addEventListener('contextmenu', stopNative, true);
    canvas.addEventListener(
      'auxclick',
      (e) => {
        if (e.button !== 0) stopNative(e);
      },
      true,
    );
    // Critical for tablets: without preventDefault, Safari cancels the pointer mid-drag.
    canvas.addEventListener(
      'touchstart',
      (e) => {
        e.preventDefault();
      },
      { passive: false },
    );
    canvas.addEventListener(
      'touchmove',
      (e) => {
        e.preventDefault();
      },
      { passive: false },
    );

    canvas.addEventListener(
      'pointerdown',
      (e) => {
        this.audio.ensureStarted();
        // Stop scroll / browser gestures — required on iPad/Android.
        e.preventDefault();
        const pos = getPos(e);
        if (e.button === 2) {
          e.stopPropagation();
          this.activePointerId = e.pointerId;
          try {
            canvas.setPointerCapture(e.pointerId);
          } catch {
            /* ignore */
          }
          this.erasing = true;
          this.lastEraseKey = null;
          this.eraseCellAt(this.pickCell(pos.x, pos.y));
          return;
        }
        if (!isPrimaryDraw(e) && e.button !== 0) {
          return;
        }
        this.activePointerId = e.pointerId;
        try {
          canvas.setPointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
        this.onPointerDown(pos.x, pos.y);
      },
      { passive: false },
    );

    canvas.addEventListener(
      'pointermove',
      (e) => {
        if (this.activePointerId !== null && e.pointerId !== this.activePointerId) return;
        if (this.drawing || this.erasing) e.preventDefault();
        const pos = getPos(e);
        this.onPointerMove(pos.x, pos.y);
      },
      { passive: false },
    );

    canvas.addEventListener('pointerup', (e) => {
      if (this.activePointerId !== null && e.pointerId !== this.activePointerId) return;
      const pos = getPos(e);
      if (e.button === 2 || this.erasing) {
        e.preventDefault();
        this.erasing = false;
        this.lastEraseKey = null;
        this.activePointerId = null;
        try {
          canvas.releasePointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
        return;
      }
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      if (isPrimaryDraw(e) || this.drawing) this.onPointerUp(pos.x, pos.y);
      this.activePointerId = null;
    });

    canvas.addEventListener('pointercancel', (e) => {
      if (this.activePointerId !== null && e.pointerId !== this.activePointerId) return;
      const pos = this.lastPointer ?? getPos(e);
      this.erasing = false;
      this.lastEraseKey = null;
      // Prefer committing the stroke — cancel used to wipe the draft on every tablet flick.
      if (this.drawing) this.onPointerUp(pos.x, pos.y);
      else this.cancelDraw({ discardDraft: true });
      this.activePointerId = null;
    });
  }

  private pickCell(sx: number, sy: number): CellCoord {
    return this.renderer.camera.screenToCell(sx, sy);
  }

  private nodeAtConnectionCell(c: CellCoord): string | null {
    return resolvePortNodeId(this.sim.state, c);
  }

  private cableAtCell(c: CellCoord): { cableId: string; index: number } | null {
    const k = cellKey(c);
    for (const cable of this.sim.state.cables) {
      // Ignore the live draft — we're editing it
      if (cable.id === this.draftCableId) continue;
      const idx = cable.cells.findIndex((cell) => cellKey(cell) === k);
      if (idx >= 0) return { cableId: cable.id, index: idx };
    }
    return null;
  }

  /**
   * Erase only the hovered cell (one cable cell or a relay).
   * Never deletes a whole cable network in one click.
   */
  private eraseCellAt(cell: CellCoord): void {
    const key = cellKey(cell);
    if (this.lastEraseKey === key) return;
    this.lastEraseKey = key;

    if (this.drawing) {
      // While drawing: undo only the last cell of the draft
      if (this.path.length > 1) {
        const last = this.path[this.path.length - 1]!;
        if (last.x === cell.x && last.y === cell.y) {
          this.path.pop();
          this.syncDraftCable();
          this.audio.playSfx('alert');
        }
      }
      return;
    }
    const relay = this.sim.state.nodes.find(
      (n) => n.kind === 'relay' && n.anchor.x === cell.x && n.anchor.y === cell.y,
    );
    if (relay) {
      if (this.sim.removeRelay(relay.id)) this.audio.playSfx('alert');
      return;
    }
    const sw = this.sim.state.nodes.find(
      (n) => n.kind === 'switch' && n.anchor.x === cell.x && n.anchor.y === cell.y,
    );
    if (sw) {
      if (this.sim.removeSwitch(sw.id)) this.audio.playSfx('alert');
      return;
    }
    const portal = this.sim.state.nodes.find(
      (n) => n.kind === 'teleporter' && n.anchor.x === cell.x && n.anchor.y === cell.y,
    );
    if (portal) {
      if (this.sim.removeTeleporter(portal.id)) this.audio.playSfx('alert');
      return;
    }
    const hit = this.cableAtCell(cell);
    if (hit) {
      if (this.sim.eraseCableCell(hit.cableId, hit.index)) {
        this.audio.playSfx('alert');
      }
    }
  }

  private onPointerDown(sx: number, sy: number): void {
    const cell = this.pickCell(sx, sy);
    this.renderer.overlay.hoverCell = cell;
    // After collapse: look around (hover tooltips) but don't rebuild.
    if (this.sim.state.gameOver) return;

    if (this.tool === 'erase') {
      this.erasing = true;
      this.lastEraseKey = null;
      this.eraseCellAt(cell);
      return;
    }

    if (this.tool === 'relay') {
      const r = this.sim.placeRelay(cell);
      if (r.ok) this.audio.playSfx('connect');
      return;
    }

    if (this.tool === 'switch') {
      const r = this.sim.placeSwitch(cell);
      if (r.ok) this.audio.playSfx('connect');
      return;
    }

    if (this.tool === 'teleporter') {
      const r = this.sim.placeTeleporter(cell);
      if (r.ok) this.audio.playSfx('connect');
      return;
    }

    // Start from a building port, an existing cable cell (T-branch), or empty space.
    // Branching from a cable creates a NEW cable sharing the junction cell — we must
    // NOT merge the parent path (that caused edge_overlap and blocked "from network").
    const nodeId = this.nodeAtConnectionCell(cell);
    this.drawing = true;
    this.path = [cell];
    this.lastPointer = { x: sx, y: sy };
    this.draftCableId = null;
    this.startNodeId = nodeId;
    this.syncDraftCable();
    this.updateDraftStretch(sx, sy);
  }

  /**
   * Tip chases the pointer via cell exit zones: near a corner → diagonal,
   * near a side midpoint → horizontal/vertical. Backtrack by returning
   * onto an earlier cell of the stroke.
   */
  private chasePointer(sx: number, sy: number): void {
    if (this.path.length === 0) return;
    const world = this.renderer.camera.screenToWorld(sx, sy);
    const hover = { x: Math.floor(world.x), y: Math.floor(world.y) };
    let changed = 0;

    for (let guard = 0; guard < 96; guard++) {
      const tip = this.path[this.path.length - 1]!;

      // Backtrack: pointer returns onto an earlier cell of this stroke.
      const backIdx = this.path.findIndex((p) => p.x === hover.x && p.y === hover.y);
      if (backIdx >= 0 && backIdx < this.path.length - 1) {
        this.path.length = backIdx + 1;
        changed++;
        continue;
      }

      // Zone snap — never blindly follow `hover` (that forced ortho steps
      // when the cursor grazed a side neighbor on a diagonal stroke).
      const next = this.renderer.camera.pickNeighborToward(sx, sy, tip);
      if (next.x === tip.x && next.y === tip.y) break;
      if (!cellsAdjacent(tip, next)) break;

      const hitIdx = this.path.findIndex((p) => p.x === next.x && p.y === next.y);
      if (hitIdx >= 0) {
        if (hitIdx === this.path.length - 2) {
          this.path.pop();
          changed++;
          continue;
        }
        break;
      }

      if (
        next.x < 0 ||
        next.y < 0 ||
        next.x >= this.sim.state.gridWidth ||
        next.y >= this.sim.state.gridHeight
      ) {
        break;
      }

      this.path.push(next);
      changed++;
    }

    if (changed > 0) {
      this.audio.playSfx('draw');
      this.syncDraftCable();
    }
    this.updateDraftStretch(sx, sy);
  }

  /** Rubber-band tip: cable visibly grows inside the tip cell toward the exit. */
  private updateDraftStretch(sx: number, sy: number): void {
    if (!this.drawing || this.path.length === 0) {
      this.renderer.overlay.draftStretch = null;
      return;
    }
    const tip = this.path[this.path.length - 1]!;
    const world = this.renderer.camera.screenToWorld(sx, sy);
    const stretch = stretchTipToward(tip, world.x, world.y);
    const next = this.renderer.camera.pickNeighborToward(sx, sy, tip);
    let valid = true;
    if (next.x !== tip.x || next.y !== tip.y) {
      if (
        next.x < 0 ||
        next.y < 0 ||
        next.x >= this.sim.state.gridWidth ||
        next.y >= this.sim.state.gridHeight
      ) {
        valid = false;
      } else {
        const trial = [...this.path, next];
        const startId =
          this.startNodeId ?? this.nodeAtConnectionCell(trial[0]!);
        const endId = this.nodeAtConnectionCell(next);
        const v = validateCablePath(
          this.sim.state,
          trial,
          startId,
          endId,
          false,
          this.draftCableId,
        );
        valid = v.ok;
      }
    }
    this.renderer.overlay.draftStretch = {
      fromX: tip.x + 0.5,
      fromY: tip.y + 0.5,
      toX: stretch.x,
      toY: stretch.y,
      progress: stretch.progress,
      valid,
    };
  }

  private currentFullPath(): CellCoord[] {
    return [...this.path];
  }

  /** Commit path into the world cable-by-cable as the player draws. */
  private syncDraftCable(): void {
    const fullPath = this.currentFullPath();
    const startId =
      this.startNodeId ??
      (fullPath.length ? this.nodeAtConnectionCell(fullPath[0]!) : null);
    const endId = fullPath.length
      ? this.nodeAtConnectionCell(fullPath[fullPath.length - 1]!)
      : null;
    const r = this.sim.upsertDraftCable(fullPath, startId, endId, this.draftCableId);
    this.draftCableId = r.cableId;
    // Keep a light preview only when draft couldn't be placed yet
    if (!r.ok || !this.draftCableId) {
      const v = validateCablePath(this.sim.state, fullPath, startId, endId);
      this.renderer.overlay.previewPath = fullPath.length >= 2 ? fullPath : null;
      this.renderer.overlay.previewValid = v.ok;
    } else {
      this.renderer.overlay.previewPath = null;
    }
  }

  private onPointerMove(sx: number, sy: number): void {
    const cell = this.pickCell(sx, sy);
    this.renderer.overlay.hoverCell = cell;
    if (this.erasing) {
      this.eraseCellAt(cell);
      return;
    }
    if (!this.drawing || this.path.length === 0) return;
    this.chasePointer(sx, sy);
    this.lastPointer = { x: sx, y: sy };
  }

  private onPointerUp(sx: number, sy: number): void {
    if (!this.drawing) return;
    this.chasePointer(sx, sy);
    this.syncDraftCable();
    const fullPath = this.currentFullPath();
    const startNode =
      this.startNodeId ??
      (fullPath.length ? this.nodeAtConnectionCell(fullPath[0]!) : null);
    const endNode = fullPath.length
      ? this.nodeAtConnectionCell(fullPath[fullPath.length - 1]!)
      : null;

    if (this.draftCableId && fullPath.length >= 2) {
      this.audio.playSfx('connect');
      this.renderer.connectFlash(fullPath[0]!, fullPath[1]!);
      if (this.showTutorial && startNode && endNode && startNode !== endNode) {
        this.showTutorial = false;
        this.renderer.overlay.tutorialHighlight = false;
        this.sim.state.paused = false;
        this.callbacks.onTutorialDone();
      }
    } else if (fullPath.length < 2 && this.draftCableId) {
      this.sim.eraseCable(this.draftCableId);
    }

    // Keep committed cable; clear drawing state without discarding draft
    this.cancelDraw();
  }
}
